/**
 * Offline Sync Manager
 * Handles queueing actions when offline and syncing when back online
 */

const DB_NAME = 'orkestria-offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending-actions';

// Open IndexedDB connection
function openDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
                store.createIndex('timestamp', 'timestamp', { unique: false });
                store.createIndex('type', 'type', { unique: false });
                store.createIndex('status', 'status', { unique: false });
            }
        };
    });
}

// Add an action to the offline queue
export async function queueOfflineAction(action) {
    // If online, try to execute immediately
    if (navigator.onLine) {
        try {
            return await executeAction(action);
        } catch (error) {
            console.log('[Offline] Online but request failed, queueing action:', action.type);
        }
    }

    // Queue the action
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        const actionData = {
            ...action,
            timestamp: new Date().toISOString(),
            status: 'pending',
        };

        const request = store.add(actionData);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            console.log('[Offline] Action queued:', action.type);
            
            // Notify service worker
            if (navigator.serviceWorker?.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'ADD_OFFLINE_ACTION',
                    action: actionData,
                });
            }

            // Try to register background sync
            if ('serviceWorker' in navigator && 'sync' in window.SyncManager) {
                navigator.serviceWorker.ready.then(registration => {
                    registration.sync.register('sync-pending-actions');
                });
            }

            resolve({ queued: true, id: request.result });
        };
    });
}

// Execute an action directly
async function executeAction(action) {
    const fetchOptions = {
        method: action.method,
        headers: action.headers || {},
    };

    if (action.body && action.method !== 'GET') {
        fetchOptions.body = JSON.stringify(action.body);
        if (!fetchOptions.headers['Content-Type']) {
            fetchOptions.headers['Content-Type'] = 'application/json';
        }
    }

    const response = await fetch(action.url, fetchOptions);
    
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
}

// Get all pending actions
export async function getPendingActions() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('status');
        const request = index.getAll(IDBKeyRange.only('pending'));

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
    });
}

// Get count of pending actions
export async function getPendingCount() {
    const actions = await getPendingActions();
    return actions.length;
}

// Get all actions (pending, synced, failed)
export async function getAllActions() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
    });
}

// Clear synced actions
export async function clearSyncedActions() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('status');
        const request = index.openCursor(IDBKeyRange.only('synced'));

        request.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
                store.delete(cursor.primaryKey);
                cursor.continue();
            } else {
                resolve();
            }
        };
        request.onerror = () => reject(request.error);
    });
}

// Clear all actions
export async function clearAllActions() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
    });
}

// Force sync (when back online)
export async function forceSync() {
    if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'FORCE_SYNC' });
        return true;
    }
    return false;
}

// Create offline-capable API methods
export function createOfflineAPI(baseUrl, token) {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    return {
        // Task operations
        createTask: (projectId, taskData) => queueOfflineAction({
            type: 'CREATE_TASK',
            method: 'POST',
            url: `${baseUrl}/projects/${projectId}/tasks`,
            headers,
            body: taskData,
            description: `Créer tâche: ${taskData.title}`,
        }),

        updateTask: (taskId, taskData) => queueOfflineAction({
            type: 'UPDATE_TASK',
            method: 'PUT',
            url: `${baseUrl}/tasks/${taskId}`,
            headers,
            body: taskData,
            description: `Modifier tâche: ${taskData.title || taskId}`,
        }),

        // Notification operations
        markNotificationRead: (notificationId) => queueOfflineAction({
            type: 'MARK_NOTIFICATION_READ',
            method: 'PUT',
            url: `${baseUrl}/notifications/${notificationId}/read`,
            headers,
            description: 'Marquer notification comme lue',
        }),

        markAllNotificationsRead: () => queueOfflineAction({
            type: 'MARK_ALL_NOTIFICATIONS_READ',
            method: 'PUT',
            url: `${baseUrl}/notifications/read-all`,
            headers,
            description: 'Marquer toutes les notifications comme lues',
        }),

        // Time tracking
        createTimeEntry: (entryData) => queueOfflineAction({
            type: 'CREATE_TIME_ENTRY',
            method: 'POST',
            url: `${baseUrl}/time-entries`,
            headers,
            body: entryData,
            description: `Entrée de temps: ${entryData.hours}h`,
        }),

        // Client notes
        addClientNote: (clientId, noteData) => queueOfflineAction({
            type: 'ADD_CLIENT_NOTE',
            method: 'POST',
            url: `${baseUrl}/clients/${clientId}/notes`,
            headers,
            body: noteData,
            description: 'Ajouter note client',
        }),

        // Generic offline action
        custom: (type, method, url, body = null, description = '') => queueOfflineAction({
            type,
            method,
            url,
            headers,
            body,
            description,
        }),
    };
}

export default {
    queueOfflineAction,
    getPendingActions,
    getPendingCount,
    getAllActions,
    clearSyncedActions,
    clearAllActions,
    forceSync,
    createOfflineAPI,
};
